// they are being used idk they aren't beinf detected by eslint
import React, { useContext, useEffect, useMemo, useState } from "react";
import { WorkspaceStatus } from "models/Workspace.model";
import { PermissionsContext } from "contexts/PermissionsContext";
import { PermissionHierarchyType } from "models/Permission.model";
import { OrgContext } from "contexts/OrgContext";
import UsernameDisplayField, {
  EmailDisplayField,
  UsernameDisplayCircle,
} from "components/UsernameDisplayField";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import FlatCard from "components/UI-lib/FlatCard";
import { NotificationContext } from "contexts/NotificationContext";
import { UserContext } from "contexts/UserContext";
import { Environment } from "components/Environment/utils/types";
import { getEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { Button as GraphButton } from "components/Graphs/Button";
import { Badge } from "components/Graphs/Badge";
import { BadgeAlert } from "lucide-react";
import { Input } from "components/Graphs/Input";

interface ShareSettingsProps {
  environment: Environment;
}
const ShareSettings: React.FC<ShareSettingsProps> = (
  props: ShareSettingsProps
) => {
  const [loadingShare, setLoadingShare] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [userData, setUserData] = useState<Map<string, any>>(new Map());
  const permissionsContext = useContext(PermissionsContext);
  const orgContext = useContext(OrgContext);
  const workspaceContext = useContext(WorkspaceContext);
  const notificationContext = useContext(NotificationContext);
  const userContext = useContext(UserContext);

  const otherUsers = useMemo(() => {
    return permissionsContext.permissions.filter(
      (permission) => permission.subjectId !== userContext.me?.id
    );
  }, [permissionsContext.permissions]);

  // Load user data for all users
  useEffect(() => {
    const loadUserData = async () => {
      const newUserData = new Map();
      for (const permission of otherUsers) {
        const user = await userContext.getUser(permission.subjectId);
        if (user) {
          newUserData.set(permission.subjectId, user);
        }
      }
      setUserData(newUserData);
    };

    if (otherUsers.length > 0) {
      loadUserData();
    }
  }, [otherUsers, userContext]);

  // Filter and sort users based on search term
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = otherUsers;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = otherUsers.filter((permission) => {
        const user = userData.get(permission.subjectId);
        const username = user?.username?.toLowerCase() || "";
        const email = user?.email?.toLowerCase() || "";
        const userId = permission.subjectId.toLowerCase();

        return (
          username.includes(searchLower) ||
          email.includes(searchLower) ||
          userId.includes(searchLower)
        );
      });
    }

    // Sort by username
    return filtered.sort((a, b) => {
      const userA = userData.get(a.subjectId);
      const userB = userData.get(b.subjectId);
      const usernameA = userA?.username || a.subjectId;
      const usernameB = userB?.username || b.subjectId;

      return usernameA.localeCompare(usernameB);
    });
  }, [otherUsers, searchTerm, userData]);

  useEffect(() => {
    if (orgContext.activeOrgId && PermissionHierarchyType.Organization) {
      loadPermissions(
        PermissionHierarchyType.Organization,
        orgContext.activeOrgId
      );
    }
    return () => permissionsContext.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgContext.activeOrgId, PermissionHierarchyType.Organization]);

  const loadPermissions = async (
    hierarchyType: PermissionHierarchyType,
    hierarchyId: string
  ) => {
    // setInlineAlertMessage("");
    setLoadingShare(true);
    const res = await permissionsContext.setup(hierarchyType, hierarchyId);
    if (!res.success) {
      console.log(res);
    } else {
      console.log(res);
    }
    setLoadingShare(false);
  };

  const handleUserShare = async (subjectId: string) => {
    setLoadingUsers((prev) => new Set([...prev, subjectId]));

    const res = await workspaceContext.shareWorkspace(
      props.environment.workspaceId || "",
      [subjectId],
      orgContext.activeOrgId
    );

    setLoadingUsers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(subjectId);
      return newSet;
    });

    console.log(res);
    if (res.success) {
      notificationContext.showNotification(
        "Successfully Added User to the Instance",
        "",
        "success"
      );
    } else {
      notificationContext.showNotification(
        "Failed to add User to the Instance",
        res.message,
        "error"
      );
    }
  };

  return (
    <>
      <div className="flex flex-col justify-start items-start w-full mt-8">
        <h1 className="text-xl mb-3 font-medium text-primary dark:text-white">
          Add Users
        </h1>
        <FlatCard className="mb-3">
          {props.environment &&
          getEnvironmentStatus(props.environment) ===
            WorkspaceStatus.Running ? (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-900 dark:text-white mb-1">
                  Manage SSH access for your organization members
                  <br />
                  <div className="flex flex-row items-center">
                    <div className="flex flex-row text-xs text-secondary p-1 bg-zinc-800 rounded-md mt-1">
                      <InformationCircleIcon className="h-4 w-4 mr-1" />
                      <p>
                        This is a beta feature and users CANNOT be removed once
                        they are added
                      </p>
                    </div>
                  </div>
                </p>
              </div>
              {otherUsers.length > 0 && (
                <div className="mt-6">
                  <Input
                    type="search"
                    placeholder="Search by username, email, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-full"
                  />
                </div>
              )}
              {otherUsers.length > 0 ? (
                <div className="mt-2">
                  <TableRoot>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>User</TableHeaderCell>
                          <TableHeaderCell>Manage</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAndSortedUsers.length > 0 ? (
                          filteredAndSortedUsers.map((permission) => (
                            <TableRow
                              key={permission.subjectId}
                              data-attribute={permission.subjectId}
                            >
                              <TableCell>
                                <div className="flex flex-row items-center">
                                  <UsernameDisplayCircle
                                    userId={permission.subjectId}
                                  />
                                  <UsernameDisplayField
                                    userId={permission.subjectId}
                                    className="text-gray-900 dark:text-white ml-1 font-medium"
                                  />
                                  <div className="text-gray-900 dark:text-white">
                                    &nbsp;•&nbsp;
                                  </div>
                                  <EmailDisplayField
                                    userId={permission.subjectId}
                                    className="text-gray-900 dark:text-white text-xs"
                                  />
                                  <div className="text-gray-900 dark:text-white">
                                    &nbsp;•&nbsp;
                                  </div>
                                  <div className="text-gray-900 dark:text-white text-xs">
                                    {permission.subjectId}
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex flex-row items-center justify-start">
                                  {!!props.environment.additionalUsers &&
                                  props.environment.additionalUsers.includes(
                                    permission.subjectId
                                  ) ? (
                                    <Badge variant="neutral">
                                      Already added
                                    </Badge>
                                  ) : (
                                    <GraphButton
                                      onClick={() =>
                                        handleUserShare(permission.subjectId)
                                      }
                                      isLoading={loadingUsers.has(
                                        permission.subjectId
                                      )}
                                      disabled={loadingUsers.size > 0}
                                      variant="primary"
                                    >
                                      Add User
                                    </GraphButton>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <div className="text-center py-4">
                                <span className="text-sm text-gray-700 dark:text-secondary">
                                  No users found matching "{searchTerm}"
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableRoot>
                </div>
              ) : (
                <div className="mt-5 text-center py-4">
                  <span className="text-sm text-gray-700 dark:text-secondary">
                    No one else is in the organization to share
                  </span>
                </div>
              )}
            </>
          ) : (
            <>Workspace needs to be running in order to share</>
          )}
        </FlatCard>
        <div />
      </div>
    </>
  );
};

export default ShareSettings;
